import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { Guid, PrintUnprintedChecksResult } from "@/api/types";
import { useUnprintedChecks } from "@/hooks/useUnprintedChecks";
import { formatMoney } from "@/lib/format";
import { PrintChecksDialog, checkCount } from "./PrintChecksDialog";
import "./RegisterCheckBatchBar.css";

export interface RegisterCheckBatchBarProps {
  registerAccountId: Guid;
  /** Re-reads the register's first page after a batch printed. */
  refresh: () => Promise<void>;
}

type BarState = "loading" | "empty" | "ready" | "printed" | "error";

/** SPA navigation for a plain `<a>`: modified clicks keep the browser's own behavior (new tab, etc.). */
function followLink(path: string, event: MouseEvent<HTMLAnchorElement>): void {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }
  event.preventDefault();
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * ef-062-7 - the "Print unprinted checks" bar `Books.tsx` renders above the register list for the
 * selected account: how many flagged payments wait (the server's count and total), a confirm step,
 * and the numbered result in the order the server returned it. Printing is one POST, one
 * transaction on the server; a failure prints nothing and the bar says so.
 *
 * data-state: loading | empty | ready | printed | error.
 * @ui-check: when nothing waits render "No checks waiting to print." and NO print button.
 * @ui-check: the result list keeps the SERVER's order - never re-sorted client-side.
 */
export function RegisterCheckBatchBar({ registerAccountId, refresh }: RegisterCheckBatchBarProps) {
  const { unprinted, status, fetching, error: readError, refetch } = useUnprintedChecks(registerAccountId);
  const [confirming, setConfirming] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [result, setResult] = useState<PrintUnprintedChecksResult | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const printedRef = useRef<HTMLParagraphElement>(null);

  // Another account's result and half-finished confirm do not belong on this one.
  useEffect(() => {
    setResult(null);
    setPrintError(null);
    setConfirming(false);
  }, [registerAccountId]);

  const count = unprinted?.count ?? 0;

  let state: BarState;
  if (status === "error") state = "error";
  else if (status === "loading") state = result ? "printed" : "loading";
  // Right after a batch the count is being re-read: keep saying "printed" instead of flashing the old count.
  else if (result && (fetching || count === 0)) state = "printed";
  else state = count === 0 ? "empty" : "ready";

  async function handleConfirm(): Promise<void> {
    if (inFlight.current) return; // a double-click must not print twice
    inFlight.current = true;
    setPrinting(true);
    setPrintError(null);
    try {
      const printed = await demoApi.printUnprintedChecks(registerAccountId);
      setResult(printed);
      setConfirming(false);
      refetch();
      // The register rows pick up their numbers; the batch is already printed, so a failed re-read is not a failed print.
      await refresh().catch(() => undefined);
      printedRef.current?.focus();
    } catch (err) {
      setConfirming(false);
      setPrintError(problemMessage(err, "Could not print the checks. Nothing was printed."));
      triggerRef.current?.focus();
    } finally {
      inFlight.current = false;
      setPrinting(false);
    }
  }

  function closeDialog(): void {
    setConfirming(false);
    triggerRef.current?.focus();
  }

  const unprintable = unprinted?.unprintable ?? 0;

  return (
    <section className="check-batch" aria-label="Print unprinted checks" data-state={state}>
      {state === "loading" && (
        <p className="check-batch__text" role="status">
          Checking for checks waiting to print…
        </p>
      )}

      {state === "error" && (
        <div className="check-batch__summary">
          <p className="check-batch__error" role="alert">
            {readError ?? "EasyFarm couldn’t load this."} Checks waiting to print could not be counted.
          </p>
          <button type="button" className="btn btn--ghost" onClick={refetch}>
            Try again
          </button>
        </div>
      )}

      {state === "empty" && <p className="check-batch__text">No checks waiting to print.</p>}

      {state === "ready" && (
        <div className="check-batch__summary">
          <p className="check-batch__text">
            <strong>{checkCount(count)} waiting</strong> · total{" "}
            <span className="check-batch__money-total">{formatMoney(unprinted?.total ?? 0)}</span>
          </p>
          <button
            ref={triggerRef}
            type="button"
            className="btn btn--primary"
            aria-haspopup="dialog"
            onClick={() => {
              setPrintError(null);
              setConfirming(true);
            }}
          >
            Print unprinted checks
          </button>
        </div>
      )}

      {unprintable > 0 && (
        <p className="check-batch__note">
          {unprintable === 1 ? "1 flagged payment has" : `${unprintable} flagged payments have`} no payee, so the batch
          cannot print until a payee is added.
        </p>
      )}

      {printError && (
        <p className="check-batch__error" role="alert">
          {printError}
        </p>
      )}

      {result && (
        <>
          <p ref={printedRef} className="check-batch__printed" role="status" tabIndex={-1}>
            {result.printed === 0 ? "Nothing was waiting - no checks printed" : `Printed ${checkCount(result.printed)}`}
            {result.remaining > 0 ? ` · ${result.remaining} still waiting` : ""}
          </p>
          {result.checks.length > 0 && (
            <ul className="check-batch__results" role="list" aria-label="Printed checks">
              {result.checks.map((c) => (
                <li key={c.entryLineId} className="check-batch__result">
                  <span className="check-batch__result-payee">
                    <span className="check-batch__result-number">Check #{c.checkNumber}</span> · {c.payeeName}
                  </span>
                  <span className="check-batch__result-amount">{formatMoney(c.amount)}</span>
                  <a
                    className="check-batch__result-link"
                    href={`/books/checks/${c.entryLineId}`}
                    onClick={(e) => followLink(`/books/checks/${c.entryLineId}`, e)}
                  >
                    View check #{c.checkNumber}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {confirming && (
        <PrintChecksDialog
          count={count}
          total={unprinted?.total ?? 0}
          printing={printing}
          onCancel={closeDialog}
          onConfirm={() => void handleConfirm()}
        />
      )}
    </section>
  );
}
